import { ApolloError } from '@apollo/client';

import { TAccountGQLType } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { DbTable, LoanProductsMap } from '../../types';
import { createLoanProductsMap } from './createMaps';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useGetLoanProductsForCreditFacilityQuery } from 'packages/loanProduct/requests/requests.gql.generated';
import { useGetTAccountsQuery } from 'packages/statements/requests/ledger.gql.generated';

export const useGetCommonWipData = ({
  creditFacilityId,
}: {
  creditFacilityId: string;
}): {
  implementationId: string | null;
  creditFacilitySlug: string | null;
  facilityType: DbTable.FacilityType | null;
  tAccounts: TAccountGQLType[] | null;
  loanProductsMap: LoanProductsMap | null;
  isLoading: boolean;
  errors: ApolloError[] | undefined;
} => {
  const {
    data: creditFacilityData,
    loading: cfDataLoading,
    error: cfError,
  } = useGetCreditFacilityQuery({
    variables: { creditFacilityId },
  });

  const {
    data: tAccountsData,
    loading: taLoading,
    error: taError,
  } = useGetTAccountsQuery({
    variables: { creditFacilityId },
  });

  const {
    data: loanProductsData,
    loading: lpLoading,
    error: lpError,
  } = useGetLoanProductsForCreditFacilityQuery({
    variables: { creditFacilityId, useWip: true },
    fetchPolicy: 'network-only',
  });

  const implementationId =
    creditFacilityData?.creditFacility.implementationId ?? null;
  const creditFacilitySlug = creditFacilityData?.creditFacility.slug ?? null;
  const facilityType = creditFacilityData?.creditFacility.type ?? null;

  const tAccounts = tAccountsData?.getTAccounts ?? null;

  const loanProductsMap = loanProductsData?.creditFacility.loanProducts
    ? createLoanProductsMap(loanProductsData?.creditFacility.loanProducts)
    : null;

  const isLoading = cfDataLoading || taLoading || lpLoading;

  const errors: ApolloError[] = [];
  if (cfError) errors.push(cfError);
  if (taError) errors.push(taError);
  if (lpError) errors.push(lpError);

  return {
    implementationId,
    creditFacilitySlug,
    tAccounts,
    loanProductsMap,
    facilityType,
    isLoading,
    errors: errors.length ? errors : undefined,
  };
};
